import { useEffect, useState } from "react";
import { Headphones, RefreshCw } from "lucide-react";
import { listInputDevices, probeChannelCount } from "@/lib/audioInput";
import { mapMicError } from "@/lib/micCheck";

/**
 * Input Device Picker — populates from navigator.mediaDevices.enumerateDevices
 * after a one-shot getUserMedia to unlock device labels. Shows channel-count
 * estimate next to each device so users can spot their multi-channel interface
 * (PreSonus StudioLive, Focusrite Scarlett, etc.).
 */
export default function InputDevicePicker({
  selectedDeviceId,
  onChange,
  onChannelCountChange,
  onError,
}) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [channelCounts, setChannelCounts] = useState({}); // deviceId → count
  const [unlocked, setUnlocked] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { devices: list, error, micError } = await listInputDevices();
      if (error) {
        onError?.(micError || mapMicError(new Error(error)));
        setDevices([]);
        return;
      }
      setDevices(list);
      setUnlocked(true);
      // Probe channel counts in parallel — best-effort.
      const counts = {};
      await Promise.all(list.map(async (d) => {
        counts[d.deviceId] = await probeChannelCount(d.deviceId, 64);
      }));
      setChannelCounts(counts);
      // If no device selected and there's a multi-channel interface, auto-pick it.
      if (!selectedDeviceId && list.length > 0) {
        const best = list.reduce((acc, d) => {
          const c = counts[d.deviceId] || 1;
          return c > (counts[acc?.deviceId] || 1) ? d : acc;
        }, list[0]);
        onChange?.(best.deviceId, counts[best.deviceId] || 1);
        onChannelCountChange?.(counts[best.deviceId] || 1);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (typeof navigator !== "undefined" && navigator.mediaDevices?.addEventListener) {
      const handler = () => unlocked && refresh();
      navigator.mediaDevices.addEventListener("devicechange", handler);
      return () => navigator.mediaDevices.removeEventListener("devicechange", handler);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked]);

  const handleSelect = (e) => {
    const id = e.target.value;
    const cc = channelCounts[id] || 1;
    onChange?.(id, cc);
    onChannelCountChange?.(cc);
  };

  return (
    <div data-testid="input-device-picker" className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] px-2.5 py-1.5">
      <Headphones className="h-3.5 w-3.5 text-cyan-300" />
      <span className="font-mono text-[10px] uppercase tracking-wider text-white/55">
        Input
      </span>
      {!unlocked ? (
        <button
          type="button"
          data-testid="input-device-unlock-btn"
          onClick={refresh}
          disabled={loading}
          className="rounded-md bg-cyan-500/20 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-cyan-100 ring-1 ring-cyan-400/30 hover:bg-cyan-500/30 disabled:opacity-40"
        >
          {loading ? "Loading…" : "Detect devices"}
        </button>
      ) : (
        <>
          <select
            data-testid="input-device-select"
            value={selectedDeviceId || ""}
            onChange={handleSelect}
            className="max-w-[220px] truncate rounded-md bg-black/40 px-2 py-0.5 font-mono text-[11px] text-white ring-1 ring-white/10 focus:outline-none focus:ring-cyan-400/40"
          >
            {devices.length === 0 && <option value="">No inputs found</option>}
            {devices.map((d, idx) => {
              const cc = channelCounts[d.deviceId];
              // deviceId can be empty when permission isn't granted yet — most
              // browsers return all device records with deviceId="" until
              // getUserMedia resolves. Use idx-prefixed key to keep React's
              // reconciler happy until real ids land.
              return (
                <option key={`dev-${idx}-${d.deviceId || "anon"}`} value={d.deviceId}>
                  {`${d.label}${cc > 0 ? ` · ${cc}ch` : ""}`}
                </option>
              );
            })}
          </select>
          <button
            type="button"
            data-testid="input-device-refresh-btn"
            onClick={refresh}
            disabled={loading}
            title="Re-scan input devices"
            className="grid h-6 w-6 place-items-center rounded-md bg-white/[0.06] text-white/65 hover:bg-white/[0.12] disabled:opacity-40"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
          </button>
        </>
      )}
    </div>
  );
}
